// AI-written ATLAS options for custom entries (POST /api/ai/atlas-options).
// When a level holds something the preset tree doesn't know (a custom Endeavour,
// Goal, Aim or Today), the level below gets 10 options written for it. Results are
// cached in the sector's node under atlas.gen, keyed by level + parent text, so each
// parent is generated once and survives reloads once the sector is saved.
import { apiFetch } from '../../lib/api';
import type { Sector } from '../../domain/olmps';

export type GenLevel = 'goal' | 'aim' | 'today' | 'step' | 'principle' | 'deity';
/** atlas.gen key prefix for the "found" principles (the written ones use 'principle'). */
export const FOUND = 'principle-found';
export interface GenPath {
  endeavour: string | null;
  goal?: string | null;
  aim?: string | null;
  today?: string | null;
}

/** Cache key in atlas.gen: the level plus the text of the item above it. */
export const genKey = (level: GenLevel, parent: string) => `${level}|${parent}`;

const inflight = new Map<string, Promise<string[]>>();

export interface GenPrinciples {
  /** Written for this ladder: brief cause-and-effect lines (shown yellow). */
  formula: string[];
  /** Existing principles that fit (shown green). */
  found: string[];
}
const inflightP = new Map<string, Promise<GenPrinciples>>();

/** Principles for a custom Goal (key = the Goal text), with its aims and today as context. */
export function fetchAtlasPrinciples(sector: Sector, goal: string, path: GenPath): Promise<GenPrinciples> {
  const key = `${sector}:${genKey('principle', goal)}`;
  const existing = inflightP.get(key);
  if (existing) return existing;
  const p = apiFetch<GenPrinciples>('/api/ai/atlas-options', {
    method: 'POST',
    body: JSON.stringify({ sector, level: 'principle', path }),
  }).then((r) => ({ formula: r.formula ?? [], found: r.found ?? [] }));
  inflightP.set(key, p);
  p.finally(() => {
    if (inflightP.get(key) === p) inflightP.delete(key);
  }).catch(() => {});
  return p;
}

/** The closest SHAPE deity for a custom Endeavour, chosen by the AI from the sector's roster. */
export function fetchAtlasDeity(
  sector: Sector,
  endeavour: string,
  deities: { id: string; name: string; role: string; themes: string[] }[],
): Promise<string> {
  const key = `${sector}:deity|${endeavour}`;
  const existing = inflight.get(key);
  if (existing) return existing.then((r) => r[0] ?? '');
  const p = apiFetch<{ id: string }>('/api/ai/atlas-options', {
    method: 'POST',
    body: JSON.stringify({ sector, level: 'deity', path: { endeavour }, deities }),
  }).then((r) => (r.id ? [r.id] : []));
  inflight.set(key, p);
  p.finally(() => {
    if (inflight.get(key) === p) inflight.delete(key);
  }).catch(() => {});
  return p.then((r) => r[0] ?? '');
}

export function fetchAtlasOptions(sector: Sector, level: GenLevel, parent: string, path: GenPath): Promise<string[]> {
  const key = `${sector}:${genKey(level, parent)}`;
  const existing = inflight.get(key);
  if (existing) return existing;
  const p = apiFetch<{ options: string[] }>('/api/ai/atlas-options', {
    method: 'POST',
    body: JSON.stringify({ sector, level, path }),
  }).then((r) => r.options ?? []);
  inflight.set(key, p);
  // Settled (either way) → forget, so a failure can be retried; the success lives in atlas.gen.
  p.finally(() => {
    if (inflight.get(key) === p) inflight.delete(key);
  }).catch(() => {});
  return p;
}
